import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../context/AuthContext';

const BATCH_INTERVAL = 15000; // flush every 15s
const RAGE_THRESHOLD = 3; // 3 clicks within 1.5s in same area
const RAGE_WINDOW = 1500;
const IDLE_TIMEOUT = 60000; // 1 min idle detection

function genSessionId() {
  return 'sess_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

export function useSessionTracker() {
  const location = useLocation();
  const bufferRef = useRef([]);
  const sessionIdRef = useRef(null);
  const clickLogRef = useRef([]);
  const scrollRef = useRef(0);
  const idleTimerRef = useRef(null);
  const flushTimerRef = useRef(null);

  // Init session ID in sessionStorage
  useEffect(() => {
    let sid = sessionStorage.getItem('bs_session_id');
    if (!sid) {
      sid = genSessionId();
      sessionStorage.setItem('bs_session_id', sid);
    }
    sessionIdRef.current = sid;
  }, []);

  // Flush buffer to backend
  const flush = useCallback(async () => {
    const events = bufferRef.current.splice(0);
    if (!events.length || !sessionIdRef.current) return;
    try {
      await api.post('/sessions/events', {
        session_id: sessionIdRef.current,
        events,
        page_url: window.location.href,
      });
    } catch {
      // Re-queue on failure (drop if too large)
      if (bufferRef.current.length < 200) {
        bufferRef.current.unshift(...events);
      }
    }
  }, []);

  const push = useCallback((type, data = {}) => {
    bufferRef.current.push({
      type,
      timestamp: new Date().toISOString(),
      page: window.location.pathname,
      ...data,
    });
  }, []);

  // Page view tracking
  useEffect(() => {
    push('page_view', { meta: { referrer: document.referrer } });
    scrollRef.current = 0;
  }, [location.pathname, push]);

  // Click tracking + rage click detection
  useEffect(() => {
    const handler = (e) => {
      const target =
        e.target.closest('[data-testid]')?.getAttribute('data-testid') ||
        e.target.tagName.toLowerCase() +
          (e.target.className ? '.' + String(e.target.className).split(' ')[0] : '');

      const now = Date.now();
      const rect = e.target.getBoundingClientRect();
      const x = Math.round(rect.left + rect.width / 2);
      const y = Math.round(rect.top + rect.height / 2);

      // Rage detection
      clickLogRef.current.push({ time: now, x, y });
      clickLogRef.current = clickLogRef.current.filter((c) => now - c.time < RAGE_WINDOW);
      const nearby = clickLogRef.current.filter(
        (c) => Math.abs(c.x - x) < 50 && Math.abs(c.y - y) < 50
      );

      if (nearby.length >= RAGE_THRESHOLD) {
        push('rage_click', { target, x, y });
        clickLogRef.current = [];
      } else {
        push('click', { target, x, y });
      }
    };

    document.addEventListener('click', handler, { capture: true, passive: true });
    return () => document.removeEventListener('click', handler, { capture: true });
  }, [push]);

  // Scroll depth tracking (throttled)
  useEffect(() => {
    let ticking = false;
    const handler = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        const pct = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
        if (pct > scrollRef.current + 10) {
          scrollRef.current = pct;
          push('scroll', { scroll_pct: pct });
        }
        ticking = false;
      });
    };

    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, [push]);

  // Idle detection
  useEffect(() => {
    const resetIdle = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        push('idle', { meta: { idle_duration_ms: IDLE_TIMEOUT } });
      }, IDLE_TIMEOUT);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach((e) => window.addEventListener(e, resetIdle, { passive: true }));
    resetIdle();

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetIdle));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [push]);

  // Periodic flush + beforeunload
  useEffect(() => {
    flushTimerRef.current = setInterval(flush, BATCH_INTERVAL);

    const onUnload = () => {
      push('session_end');
      const events = bufferRef.current.splice(0);
      if (events.length && sessionIdRef.current) {
        const token = localStorage.getItem('bidsentry_token');
        const blob = new Blob(
          [JSON.stringify({ session_id: sessionIdRef.current, events, token })],
          { type: 'application/json' }
        );
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/sessions/beacon`;
        navigator.sendBeacon(url, blob);
      }
    };

    window.addEventListener('beforeunload', onUnload);

    return () => {
      clearInterval(flushTimerRef.current);
      window.removeEventListener('beforeunload', onUnload);
      flush();
    };
  }, [flush, push]);
}
